export type ListType = 'want_to_read' | 'recommend';
export type Phase = 'nominating' | 'voting';
export type ReadingHistoryStatus = 'finished' | 'dnf' | 'skipped';
export type SpoilerScope = 'spoiler_free' | 'whole_book';

export const GENRE_OPTIONS = [
	'Classics',
	'Contemporary',
	'Crime & Mystery',
	'Fantasy',
	'Graphic Novels',
	'Historical Fiction',
	'Horror',
	'Humour',
	'Literary Fiction',
	'Memoir & Biography',
	'Non-fiction',
	'Poetry',
	'Romance',
	'Science Fiction',
	'Short Stories',
	'Thriller',
	'Travel',
	'Young Adult',
] as const;

export type SuggestedGenre = (typeof GENRE_OPTIONS)[number];
export type Genre = string;

export interface Member {
	id: string;
	name: string;
	accent: string;
	sortOrder: number;
}

export interface Book {
	id: string;
	openLibraryKey: string | null;
	title: string;
	author: string;
	coverUrl: string | null;
	firstPublishYear: number | null;
	sourceUrl: string | null;
	genres: Genre[];
}

export interface SearchBook extends Omit<Book, 'id'> {
	id?: string;
}

export interface ListItem {
	id: string;
	memberId: string;
	listType: ListType;
	note: string;
	addedAt: string;
	book: Book;
}

export interface PhaseCheck {
	memberId: string;
	phase: Phase;
	completed: boolean;
}

export interface Nomination {
	id: string;
	nominatedBy: string;
	createdAt: string;
	votedByActiveMember: boolean;
	book: Book;
}

export interface PodiumEntry {
	nominationId: string;
	rank: 1 | 2 | 3;
	votes: number;
	tieBreak: boolean;
	book: Book;
}

export interface HistoricalBallot {
	cycleId: string;
	selectedNominationId: string;
	podium: PodiumEntry[];
	remainingBooks: Book[];
}

export interface Meeting {
	id: string;
	venue: string | null;
	startsAt: string | null;
	notes: string;
	book: Book;
	readingStatus: Array<{ memberId: string; completed: boolean }>;
	dateOptions: DatePollOption[];
	questionJar: QuestionJar;
}

export interface DatePollOption {
	id: string;
	startsAt: string;
	createdBy: string;
	responses: Array<{ memberId: string; canAttend: boolean; canHost: boolean }>;
}

export interface SealedQuestion {
	id: string;
	memberId: string;
	question: string | null;
	spoilerScope: SpoilerScope;
	createdAt: string;
	isOwn: boolean;
}

export interface QuestionJar {
	revealed: boolean;
	questions: SealedQuestion[];
}

export interface ReadHistoryReaction {
	memberId: string;
	status: ReadingHistoryStatus | null;
	rating: number | null;
	verdict: string;
}

export interface ReadHistoryEntry {
	id: string;
	meetingId: string | null;
	meetingDate: string | null;
	venue: string | null;
	notes: string;
	book: Book;
	reactions: ReadHistoryReaction[];
	questionJar: QuestionJar | null;
	selection: HistoricalBallot | null;
	automaticallyArchived: boolean;
}

export interface LastRollover {
	id: string;
	rolledOverAt: string;
	previousBook: Book;
	canUndo: boolean;
	blockedReason: string | null;
}

export interface ActivityItem {
	id: string;
	memberId: string | null;
	message: string;
	createdAt: string;
}

export interface BootstrapData {
	members: Member[];
	activeMemberId: string;
	lists: ListItem[];
	memberGenres: Record<string, Genre[]>;
	history: ReadHistoryEntry[];
	cycle: {
		id: string;
		label: string;
		checks: PhaseCheck[];
		allNominationsComplete: boolean;
		allVotingComplete: boolean;
		nominations: Nomination[];
		podium: PodiumEntry[];
		remainingNominationIds: string[];
		selectedNominationId: string | null;
		selectedAt: string | null;
		selectedBy: string | null;
	};
	meeting: Meeting;
	lastRollover: LastRollover | null;
	activity: ActivityItem[];
	updatedAt: string;
}

export interface ApiErrorBody {
	error: string;
}
